import { AfterViewInit, Component, ElementRef, forwardRef, viewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

/**
 * Elements kept in the editor output. Anything else is unwrapped (its text
 * content is kept, the element itself is dropped), except elements in
 * DROP_WITH_CONTENT whose whole subtree is discarded.
 */
const ALLOWED_TAGS = new Set(['p', 'br', 'blockquote', 'ul', 'ol', 'li', 'a', 'strong', 'b', 'em', 'i', 'u', 's']);

const DROP_WITH_CONTENT = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'select', 'textarea',
  'img', 'video', 'audio', 'svg', 'canvas', 'link', 'meta', 'title', 'head'
]);

// only absolute http(s) and mailto links are allowed
const SAFE_HREF = /^(https?:\/\/|mailto:)/i;

/**
 * Reduces an arbitrary HTML string to the limited set of elements/attributes
 * supported by the editor: paragraphs, line breaks, quotes, lists, inline
 * formatting and safe links. No styling attributes, no scripts, no images.
 */
export function sanitizeHtml(html: string): string {
  if (!html) {
    return '';
  }
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) {
    return '';
  }
  const clean = doc.createElement('div');
  for (const node of Array.from(root.childNodes)) {
    const cleaned = cleanNode(node, doc);
    if (cleaned) {
      clean.appendChild(cleaned);
    }
  }
  return clean.innerHTML;
}

function cleanNode(node: Node, doc: Document): Node | null {
  if (node.nodeType === Node.TEXT_NODE) {
    return doc.createTextNode(node.textContent ?? '');
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null; // comments, processing instructions, ...
  }
  const el = node as HTMLElement;
  const tag = el.tagName.toLowerCase();
  if (DROP_WITH_CONTENT.has(tag)) {
    return null;
  }
  // contenteditable wraps lines in divs; normalize them to paragraphs
  const targetTag = tag === 'div' ? 'p' : tag;
  if (!ALLOWED_TAGS.has(targetTag)) {
    // unwrap: keep the children only
    const fragment = doc.createDocumentFragment();
    for (const child of Array.from(el.childNodes)) {
      const cleaned = cleanNode(child, doc);
      if (cleaned) {
        fragment.appendChild(cleaned);
      }
    }
    return fragment;
  }
  const clean = doc.createElement(targetTag);
  if (targetTag === 'a') {
    const href = (el.getAttribute('href') ?? '').trim();
    if (SAFE_HREF.test(href)) {
      clean.setAttribute('href', href);
    }
    // unsafe or missing href: keep the link text without the attribute
  }
  for (const child of Array.from(el.childNodes)) {
    const cleaned = cleanNode(child, doc);
    if (cleaned) {
      clean.appendChild(cleaned);
    }
  }
  return clean;
}

@Component({
  selector: 'app-html-editor',
  imports: [
    MatButtonModule,
    MatIconModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HtmlEditorComponent),
      multi: true
    }
  ],
  templateUrl: './html-editor.component.html',
  styleUrl: './html-editor.component.scss',
})
export class HtmlEditorComponent implements ControlValueAccessor, AfterViewInit {
  private readonly editorRef = viewChild.required<ElementRef<HTMLElement>>('editor');

  disabled = false;

  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  private viewReady = false;
  private pendingValue: string | null = null;
  private lastEmitted = '';

  ngAfterViewInit(): void {
    this.viewReady = true;
    if (this.pendingValue !== null) {
      this.setHtml(this.pendingValue);
      this.pendingValue = null;
    }
  }

  writeValue(value: string | null): void {
    const sanitized = sanitizeHtml(value ?? '');
    this.pendingValue = sanitized;
    if (this.viewReady) {
      this.setHtml(sanitized);
      this.pendingValue = null;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onInput(): void {
    const el = this.editorRef().nativeElement;
    const raw = el.innerHTML;
    const clean = sanitizeHtml(raw);
    if (clean !== raw) {
      // the DOM contained something not allowed; rewrite it cleanly
      el.innerHTML = clean;
    }
    if (clean !== this.lastEmitted) {
      this.lastEmitted = clean;
      this.onChange(clean);
    }
  }

  markTouched(): void {
    this.onTouched();
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const html = event.clipboardData?.getData('text/html');
    if (html) {
      document.execCommand('insertHTML', false, sanitizeHtml(html));
    } else {
      const text = event.clipboardData?.getData('text/plain') ?? '';
      document.execCommand('insertText', false, text);
    }
    this.onInput();
  }

  exec(command: string, value?: string): void {
    if (this.disabled) {
      return;
    }
    this.editorRef().nativeElement.focus();
    document.execCommand(command, false, value);
    this.onInput();
  }

  toggleBlockquote(): void {
    const current = document.queryCommandValue('formatBlock');
    this.exec('formatBlock', current === 'blockquote' ? 'p' : 'blockquote');
  }

  addLink(): void {
    const url = window.prompt('Link URL (https://... or mailto:...)');
    if (url && SAFE_HREF.test(url.trim())) {
      this.exec('createLink', url.trim());
    }
  }

  private setHtml(html: string): void {
    const el = this.editorRef().nativeElement;
    if (el.innerHTML !== html) {
      el.innerHTML = html;
    }
    this.lastEmitted = html;
  }
}
